<template lang="html">
	<div class="map-container">
		<div class="map-toolbar">
			<v-btn-toggle v-model="activeLayer" mandatory dense class="layer-toggle">
				<v-btn value="lme" small>Large Marine Ecosystems</v-btn>
				<v-btn value="basin" small>Ocean Subbasins</v-btn>
			</v-btn-toggle>
		</div>
		<l-map
			:zoom="zoom"
			:center="center"
			:options="{ zoomSnap: 0.25, zoomDelta: 0.25 }"
			@zoom="$store.mapZoom = $event.target.getZoom()"
			@moveend="$store.mapCenter = $event.target.getCenter()"
			class="map"
			ref="map"
		>
			<l-tile-layer :url="url" :attribution="attribution" />
			<l-geo-json
				v-if="activeLayer === 'lme' && lmeGeojson"
				:key="'lme-' + selectedKey"
				:geojson="lmeGeojson"
				:options="lmeOptions"
			/>
			<l-geo-json
				v-if="activeLayer === 'basin' && subbasinsGeojson"
				:key="'basin-' + selectedKey"
				:geojson="subbasinsGeojson"
				:options="subbasinsOptions"
			/>
		</l-map>
	</div>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import { LMap, LTileLayer, LGeoJson } from 'vue2-leaflet'

export default {
	components: { LMap, LTileLayer, LGeoJson },

	data() {
		return {
			url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
			attribution:
				'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
			activeLayer: 'lme',
			lmeGeojson: null,
			subbasinsGeojson: null,
		}
	},

	computed: {
		zoom() {
			return this.$store.mapZoom
		},
		center() {
			return this.$store.mapCenter
		},
		selectedKey() {
			return `${this.$store.selectedLmeBasin}-${this.$store.selectedOcean}-${this.$store.selectedRegion}`
		},
		lmeOptions() {
			return {
				style: this.lmeStyle,
				onEachFeature: this.onEachFeature,
				filter: this.filterFeature,
			}
		},
		subbasinsOptions() {
			return {
				style: this.subbasinsStyle,
				onEachFeature: this.onEachFeature,
				filter: this.filterFeature,
			}
		},
	},

	methods: {
		toKey(s) {
			return s.toLowerCase().replace(/[^a-z0-9]/g, '')
		},

		parseAreaNames(areaNames) {
			if (!areaNames || areaNames === 'excluded') return null
			const parts = areaNames.split(':')
			if (parts.length < 3) return null

			const typeMap = { LME: 'lme', BASIN: 'basin' }
			const lmebasin = typeMap[parts[0]]
			if (!lmebasin) return null

			return {
				lmebasin,
				ocean: this.toKey(parts[1]),
				region: this.toKey(parts[2]),
			}
		},

		getRegionObj(params) {
			if (!params) return null
			const tree = this.$store.menuTree
			return (
				(tree[params.lmebasin] &&
					tree[params.lmebasin].children &&
					tree[params.lmebasin].children[params.ocean] &&
					tree[params.lmebasin].children[params.ocean].children &&
					tree[params.lmebasin].children[params.ocean].children[
						params.region
					]) ||
				null
			)
		},

		getStripeUrl(params) {
			const regionObj = this.getRegionObj(params)
			if (!regionObj) return null
			const measure = this.$store.selectedMeasure || 'ph'
			const measureData = regionObj[measure] || regionObj.ph
			return measureData ? measureData.s : null
		},

		isSelected(feature) {
			const params = this.parseAreaNames(feature.properties.area_names)
			if (!params) return false
			return (
				params.lmebasin === this.$store.selectedLmeBasin &&
				params.ocean === this.$store.selectedOcean &&
				params.region === this.$store.selectedRegion
			)
		},

		hasData(feature) {
			const params = this.parseAreaNames(feature.properties.area_names)
			return !!this.getStripeUrl(params)
		},

		lmeStyle(feature) {
			const selected = this.isSelected(feature)
			const noData = !this.hasData(feature)
			if (noData) {
				return {
					fillColor: '#555',
					weight: 1,
					color: '#777',
					fillOpacity: 0.25,
					dashArray: '4',
				}
			}
			return {
				fillColor: selected ? '#f4a261' : '#00b4d8',
				weight: selected ? 2 : 1,
				color: selected ? '#fff' : '#90e0ef',
				fillOpacity: selected ? 0.65 : 0.35,
			}
		},

		subbasinsStyle(feature) {
			const selected = this.isSelected(feature)
			const noData = !this.hasData(feature)
			if (noData) {
				return {
					fillColor: '#555',
					weight: 1,
					color: '#777',
					fillOpacity: 0.25,
					dashArray: '4',
				}
			}
			return {
				fillColor: selected ? '#f4a261' : '#4cc9f0',
				weight: selected ? 2 : 1,
				color: selected ? '#fff' : '#caf0f8',
				fillOpacity: selected ? 0.65 : 0.3,
			}
		},

		filterFeature(feature) {
			const areaNames = feature.properties.area_names
			return !!areaNames && areaNames !== 'excluded'
		},

		onEachFeature(feature, layer) {
			const name = feature.properties.pretty_nam
			if (!name || name === 'excluded') return

			const params = this.parseAreaNames(feature.properties.area_names)
			const stripeUrl = this.getStripeUrl(params)

			if (stripeUrl) {
				const escapedName = name
					.replace(/&/g, '&amp;')
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;')
				const popupHtml = `<div class="oas-popup" style="background-image:url('${stripeUrl}')"><span class="oas-popup-name">${escapedName}</span></div>`
				layer.bindPopup(popupHtml, {
					maxWidth: 280,
					className: 'oas-popup-wrapper',
					closeButton: false,
					autoPan: false,
				})
				layer.on({
					mouseover: (e) => {
						e.target.setStyle({ weight: 2.5, fillOpacity: 0.6, color: '#fff' })
						layer.openPopup(e.latlng)
					},
					mousemove: (e) => {
						layer.getPopup().setLatLng(e.latlng)
					},
					mouseout: (e) => {
						const style =
							this.activeLayer === 'lme'
								? this.lmeStyle(feature)
								: this.subbasinsStyle(feature)
						e.target.setStyle(style)
						layer.closePopup()
					},
					click: () => this.navigateToRegion(feature),
				})
			} else {
				layer.bindTooltip(name + ' (no data)', { sticky: true })
				layer.on({
					mouseover: (e) => {
						e.target.setStyle({ weight: 2.5, fillOpacity: 0.6, color: '#fff' })
					},
					mouseout: (e) => {
						const style =
							this.activeLayer === 'lme'
								? this.lmeStyle(feature)
								: this.subbasinsStyle(feature)
						e.target.setStyle(style)
					},
					click: (e) => L.DomEvent.stopPropagation(e),
				})
				layer.options.className = 'oas-no-data'
				layer.getElement()?.style.setProperty('cursor', 'default')
				layer.getElement()?.setAttribute('tabindex', '-1')
				layer.getElement()?.setAttribute('focusable', 'false')
				layer.getElement()?.addEventListener('mousedown', (e) => e.preventDefault())
			}
		},

		navigateToRegion(feature) {
			const params = this.parseAreaNames(feature.properties.area_names)
			if (!params) return

			this.$store.selectedLmeBasin = params.lmebasin
			this.$store.selectedOcean = params.ocean
			this.$store.selectedRegion = params.region

			this.$router.push({
				name: 'stripes',
				params: {
					stripeType: this.$store.selectedVariant || 's',
					lmebasin: params.lmebasin,
					ocean: params.ocean,
					region: params.region,
					pH: this.$store.selectedMeasure || 'ph',
				},
			})
		},

		async loadGeojson() {
			const [lmeRes, subRes] = await Promise.all([
				fetch('/regions_large_marine_ecosystems.geojson'),
				fetch('/regions_ocean_subbasins.geojson'),
			])
			this.lmeGeojson = await lmeRes.json()
			this.subbasinsGeojson = await subRes.json()
		},
	},

	mounted() {
		this.loadGeojson()

		if (this.$store.firstMapRun) {
			this.$nextTick(() => {
				if (this.$refs.map && this.$refs.map.mapObject) {
					this.$refs.map.mapObject.fitBounds([
						[-75, -180],
						[80, 180],
					])
				}
				this.$store.firstMapRun = false
			})
		}

		// Sync layer toggle to current lmebasin type
		const lmebasin = this.$store.selectedLmeBasin
		if (lmebasin === 'basins' || lmebasin === 'basin') {
			this.activeLayer = 'basin'
		} else {
			this.activeLayer = 'lme'
		}
	},
}
</script>

<style>
.map-container {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	padding: 1rem;
}

.map-toolbar {
	display: flex;
	justify-content: center;
	padding-bottom: 0.75rem;
}

.layer-toggle {
	background: rgba(255, 255, 255, 0.05) !important;
}

.map {
	flex: 1 1 auto;
	min-height: 500px;
	z-index: 1;
	border: 0.4rem solid rgba(0, 0, 0, 0.75);
}

/* Stripe-background popups */
.oas-popup-wrapper .leaflet-popup-content-wrapper {
	padding: 0;
	overflow: hidden;
	border-radius: 10px;
}

.oas-popup-wrapper .leaflet-popup-content {
	margin: 0;
}

.oas-popup-wrapper .leaflet-popup-close-button span {
	color: red;
	text-shadow: -1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black,
		1px 1px 0 black;
}

.oas-popup {
	background-size: cover;
	background-position: center;
	width: 260px;
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
}

.oas-popup-name {
	font-weight: bold;
	font-size: 1rem;
	color: black;
	text-align: center;
	padding: 0 0.5rem;
	text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white,
		1px 1px 0 white;
}

.oas-no-data *,
.oas-no-data {
    cursor: default !important;
    pointer-events: none; /* optional: fully blocks interaction */
	user-select: none; /* optional: prevents text selection */
	outline: none; /* optional: removes focus outline */
}
</style>
